import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { exportResultsFromStorageCsv, exportResultsFromStorageJsonl } from '../../utils/export';
import { ungzip } from 'pako';

const JobDashboard: React.FC<{ jobId: string }> = ({ jobId }) => {
  const [job, setJob] = useState<any>(null);
  const [resultPreview, setResultPreview] = useState<{ text: string; category: string }[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const { data, error } = await supabase
          .from('jobs')
          .select('id, result_uri, status, created_at')
          .eq('id', jobId)
          .single();
        if (error) throw error;
        setJob(data);
        // Fetch a small preview from Storage (first ~100 lines)
        if (data?.result_uri) {
          try {
            const { data: fileData, error: downloadErr } = await supabase.storage.from('jobs').download(data.result_uri);
            if (!downloadErr && fileData) {
              const buf = await fileData.arrayBuffer();
              const text = new TextDecoder('utf-8').decode(ungzip(new Uint8Array(buf)));
              const lines = text.split('\n').filter(Boolean).slice(0, 100);
              const rows = lines.map(l => JSON.parse(l)).filter((r: any) => typeof r?.text === 'string' && typeof r?.category === 'string');
              setResultPreview(rows);
            }
          } catch {}
        } else {
          setResultPreview(null);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load job');
      }
    })();
  }, [jobId]);

  const counts = useMemo(() => {
    const map = new Map<string, number>();
    const arr = Array.isArray(resultPreview) ? resultPreview : [];
    for (const r of arr) map.set(r.category, (map.get(r.category) || 0) + 1);
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [resultPreview]);

  if (error) return <div style={{ color: '#ff6b6b' }}>{error}</div>;
  if (!job) return <div>Loading…</div>;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="card" style={{ padding: 16 }}>
        <h3 style={{ margin: 0 }}>Job {jobId.slice(0, 8)}</h3>
        <div style={{ color: 'var(--color-text-muted)' }}>{job.status} • {new Date(job.created_at).toLocaleString()}</div>
      </div>

      <div className="card" style={{ padding: 16 }}>
        <h4 style={{ marginTop: 0 }}>Results</h4>
        <div style={{ border: '1px solid var(--color-border)', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#0f111a' }}>
                <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>Category</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>Count</th>
              </tr>
            </thead>
            <tbody>
              {counts.map(([k, v]) => (
                <tr key={k}>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>{k}</td>
                  <td style={{ padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }}>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="button" disabled={!job?.result_uri} onClick={() => exportResultsFromStorageCsv(job.id, job.result_uri, 'single')}>Export CSV</button>
          <button className="button secondary" disabled={!job?.result_uri} onClick={() => exportResultsFromStorageCsv(job.id, job.result_uri, 'onehot')}>Export one-hot</button>
          <button className="button secondary" disabled={!job?.result_uri} onClick={() => exportResultsFromStorageJsonl(job.id, job.result_uri)}>Download .ndjson.gz</button>
        </div>
      </div>
    </div>
  );
};

export default JobDashboard; 